import { sortBy } from 'lodash-es';
import { makeAutoObservable, runInAction } from 'mobx';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { Department } from '~hooks/useDepartment/models';
import { Material } from '~hooks/useMaterials/models';
import { Rate } from '~hooks/useRates/models';
import { Service } from '~hooks/useServices/models';
import { Geofence } from '~hooks/useSites/models';
import { Filters as AccountFilters } from '~pages/Settings/Accounts/useAccountFilters';
import { Filters as DriverFilters } from '~pages/Settings/Administration/Drivers/hooks/useDriverFilters';
import { Filters as EquipmentFilters } from '~pages/Settings/Administration/Equipment/hooks/useEquipmentFilters';
import { Filters as SitesFilters } from '~pages/Settings/Administration/Sites/hooks/useSitesFilters';
import { Filters as UserFilters } from '~pages/Settings/UserManagement/hooks/useUserFilters';
import { queryClient } from '~queries/queryClient';
import { RATE_DEPARTMENT_MATCH_QUERY_KEY } from '~queries/useMissingRateDepartmentNamesQuery';
import { Pagination, PaginationLink } from '~services/pagination';
import { RootStore } from '~store/index';
import { getPageNumber } from '~utils/pagination';

class CompanyAssetsStore {
  materials: Material[] = [];
  // a create or edit elsewhere can put a single material in the list before any full load
  hasLoadedMaterials = false;
  services: Service[] = [];
  rates: Rate[] = [];
  siteFilters: SitesFilters = {};
  equipmentFilters: EquipmentFilters = {};
  driverFilters: DriverFilters = {};
  userFilters: UserFilters = {};
  activeSiteAdditionalGeoFences: Geofence[] = [];
  accountFilters: AccountFilters = {};
  departments: Department[] = [];
  companyShares: CompanyShare[] = [];

  ratesPagination: Pagination = {
    limit: MAX_API_PAGE_SIZE,
    after: '',
    before: '',
    page: 1,
  };

  driverAvailability: Record<string, boolean> = {};

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }
  setActiveSiteAdditionalGeoFences(geoFences: Geofence[]) {
    this.activeSiteAdditionalGeoFences = geoFences;
  }
  setMaterials(materials: Material[]) {
    this.materials = [...materials];
    this.hasLoadedMaterials = true;
  }
  updateMaterial = (material: Material) => {
    this.materials = this.materials.map((m) => (m.id === material.id ? material : m));
  };
  deleteMaterial = (materialId: string) => {
    this.materials = this.materials.filter((m) => m.id !== materialId);
  };
  addMaterial = (material: Material) => {
    const idx = this.materials.findIndex((m) => m.id === material.id);
    if (idx === -1) {
      this.materials = [...this.materials, material];
    } else {
      this.materials[idx] = material;
      this.materials = [...this.materials];
    }
  };

  public get allMaterials() {
    return sortBy(this.materials, (item) => item.type.name);
  }

  setRatesPagination(pagination: Pagination) {
    this.ratesPagination = {
      ...this.ratesPagination,
      before: pagination.before || '',
      after: pagination.after || '',
    };
  }
  updateRatesPageNumber(link: PaginationLink) {
    runInAction(() => {
      this.ratesPagination = {
        ...this.ratesPagination,
        page: getPageNumber(this.ratesPagination.page, link),
      };
    });
  }
  setAccountFilters = (filters: AccountFilters, merge?: boolean) => {
    if (merge) {
      this.accountFilters = { ...this.accountFilters, ...filters };
    } else {
      this.accountFilters = filters;
    }
  };
  setRates(rates: Rate[]) {
    this.rates = [...rates];
  }
  updateRate = (rate: Rate) => {
    this.rates = this.rates.map((r) => (r.id === rate.id ? rate : r));
  };
  deleteRate = (rateId: string) => {
    this.rates = this.rates.filter((r) => r.id !== rateId);
  };
  addRate = (rate: Rate) => {
    const idx = this.rates.findIndex((r) => r.id === rate.id);

    if (idx === -1) {
      this.rates = [...this.rates, rate];
    } else {
      this.rates[idx] = rate;
      this.rates = [...this.rates];
    }
  };
  setServices(services: Service[]) {
    this.services = [...sortBy(services, 'type', 'name')];
  }

  setSiteFilters = (filters: SitesFilters, merge?: boolean) => {
    if (merge) {
      this.siteFilters = { ...this.siteFilters, ...filters };
    } else {
      this.siteFilters = filters;
    }
  };

  setEquipmentFilters = (filters: EquipmentFilters, merge?: boolean) => {
    if (merge) {
      this.equipmentFilters = { ...this.equipmentFilters, ...filters };
    } else {
      this.equipmentFilters = filters;
    }
  };

  // Todo: this is a ideal place for other company assets, i.e. Locations, Vendors, etc.

  // Departments management
  setDepartments(departments: Department[]) {
    this.departments = [...departments];
  }

  addDepartment = (department: Department) => {
    this.departments = [...this.departments, department];
    void queryClient.invalidateQueries({ queryKey: [RATE_DEPARTMENT_MATCH_QUERY_KEY] });
  };

  updateDepartment = (department: Department) => {
    this.departments = this.departments.map((d) =>
      d.id === department.id ? department : d,
    );
    void queryClient.invalidateQueries({ queryKey: [RATE_DEPARTMENT_MATCH_QUERY_KEY] });
  };

  deleteDepartment = (departmentId: string) => {
    this.departments = this.departments.filter((d) => d.id !== departmentId);
    void queryClient.invalidateQueries({ queryKey: [RATE_DEPARTMENT_MATCH_QUERY_KEY] });
  };

  setCompanyShares(companyShares: CompanyShare[]) {
    this.companyShares = [...companyShares];
  }

  addCompanyShare = (companyShare: CompanyShare) => {
    this.companyShares = [...this.companyShares, companyShare];
  };

  updateCompanyShare = (companyShare: CompanyShare) => {
    this.companyShares = this.companyShares.map((companyShareItem) =>
      companyShareItem.id === companyShare.id ? companyShare : companyShareItem,
    );
  };

  deleteCompanyShare = (companyShareId: string) => {
    this.companyShares = this.companyShares.filter(
      (companyShare) => companyShare.id !== companyShareId,
    );
  };

  setDriverAvailability(driverId: string, availability: boolean) {
    this.driverAvailability[driverId] = availability;
  }

  setDriverFilters = (filters: DriverFilters, merge?: boolean) => {
    if (merge) {
      this.driverFilters = { ...this.driverFilters, ...filters };
    } else {
      this.driverFilters = filters;
    }
  };

  setUserFilters = (filters: UserFilters, merge?: boolean) => {
    if (merge) {
      this.userFilters = { ...this.userFilters, ...filters };
    } else {
      this.userFilters = filters;
    }
  };
}

export default CompanyAssetsStore;
